// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock,
  HelpCircle,
  Info,
  PhoneIncoming,
  Radio,
  Wifi,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils/cn';
import { getApiCall } from '@/service/httpCall';
import {
  API_WEBRTC_SIP_TRACE_EVENTS,
  API_WEBRTC_SIP_TRACE_SESSIONS,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { FIELD_HELP, describeCode, describeEvent } from './sipEventDictionary';

const DATE_BUCKETS = [
  { key: '0-7', label: 'Last 7 days', from: 0, to: 6 },
  { key: '7-14', label: '7–14 days', from: 7, to: 13 },
  { key: '14-21', label: '14–21 days', from: 14, to: 20 },
  { key: '21-30', label: '21–30 days', from: 21, to: 29 },
];

const fmtDateTime = (v) => (v ? moment(v).format('DD MMM YYYY · HH:mm') : '—');
const fmtTime = (v) => (v ? moment(v).format('HH:mm:ss') : '—');

const fmtDuration = (ms) => {
  const n = Number(ms) || 0;
  if (!n) return null;
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)} s`;
  return `${Math.round(n / 60000)} min`;
};

const bucketRange = (bucket) => ({
  from: moment().subtract(bucket.to, 'days').startOf('day').toISOString(),
  to: moment().subtract(bucket.from, 'days').endOf('day').toISOString(),
});

const TONE = {
  good: { color: '#047857', tint: '#d1fae5', Icon: CheckCircle2 },
  bad: { color: '#dc2626', tint: '#fee2e2', Icon: AlertTriangle },
  neutral: { color: '#475569', tint: '#e2e8f0', Icon: Info },
};

/** Small "?" that explains a field without turning the UI into a manual. */
const Hint = ({ text }) =>
  text ? (
    <span
      title={text}
      className="inline-flex cursor-help text-muted-foreground/70 align-middle"
    >
      <HelpCircle className="h-3 w-3" strokeWidth={2.4} />
    </span>
  ) : null;

const Chip = ({ children, hint }) => (
  <span
    title={hint || undefined}
    className={cn(
      'inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground',
      hint && 'cursor-help',
    )}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------- timeline

function TechnicalDetails({ ev }) {
  const [open, setOpen] = useState(false);
  const rows = [
    ['Call reference', ev.sip_call_id, FIELD_HELP.sip_call_id],
    ['Platform call id', ev.call_sid, FIELD_HELP.call_sid],
    ['Session reference', ev.trace_id, FIELD_HELP.trace_id],
    ['Browser', ev.browser, FIELD_HELP.browser],
    ['Operating system', ev.os, FIELD_HELP.os],
    ['Network', ev.net_type, FIELD_HELP.net_type],
    ['Event name', ev.event_type, 'The internal name for this step.'],
  ].filter(([, v]) => v);

  let extra = null;
  try {
    extra = ev.detail ? JSON.parse(ev.detail) : null;
  } catch (e) {
    extra = null;
  }

  if (rows.length === 0 && !extra) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
      >
        <ChevronDown
          className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
          strokeWidth={2.6}
        />
        Technical details
      </button>
      {open && (
        <div className="mt-1.5 flex flex-col gap-1 rounded-lg border border-border/50 bg-muted/30 px-3 py-2">
          {rows.map(([label, value, hint]) => (
            <div key={label} className="flex flex-wrap items-baseline gap-2">
              <span className="min-w-[120px] shrink-0 text-[11px] font-semibold text-muted-foreground">
                {label} <Hint text={hint} />
              </span>
              <span className="min-w-0 flex-1 break-all text-[11px] font-medium text-foreground">
                {String(value)}
              </span>
            </div>
          ))}
          {extra &&
            Object.entries(extra).map(([k, v]) => (
              <div key={k} className="flex flex-wrap items-baseline gap-2">
                <span className="min-w-[120px] shrink-0 text-[11px] font-semibold text-muted-foreground">
                  {k.replace(/_/g, ' ')}
                </span>
                <span className="min-w-0 flex-1 break-all text-[11px] font-medium text-foreground">
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function TraceEvent({ ev, isLast }) {
  const meta = describeEvent(ev.event_type);
  const tone = ev.outcome === 'failure' ? 'bad' : meta.tone;
  const { color, tint, Icon } = TONE[tone] || TONE.neutral;
  const codeText = describeCode(ev.code, ev.code_kind);
  const duration = fmtDuration(ev.duration_ms);
  // The stored reason is already plain-ish; only show it when it adds
  // something the label does not already say.
  const showReason =
    ev.reason && ev.reason.toLowerCase() !== meta.label.toLowerCase();

  return (
    <div className="flex gap-3.5">
      <div className="flex flex-shrink-0 flex-col items-center">
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-full"
          style={{ background: tint, color }}
        >
          <Icon className="h-4 w-4" strokeWidth={2.3} />
        </span>
        {!isLast && (
          <span
            className="mt-1 w-[3px] flex-1 rounded"
            style={{ background: `${color}33` }}
          />
        )}
      </div>

      <div className={cn('min-w-0 flex-1', !isLast && 'pb-4')}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-foreground">{meta.label}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
            <Clock className="h-3 w-3" strokeWidth={2.4} />
            {fmtTime(ev.ts)}
          </span>
        </div>

        {meta.help && (
          <div className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
            {meta.help}
          </div>
        )}

        {showReason && (
          <div
            className="mt-1.5 rounded-md px-2.5 py-1.5 text-[11.5px] font-medium"
            style={{ background: tint, color }}
          >
            {ev.reason}
          </div>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {codeText && (
            <Chip hint={FIELD_HELP.code}>
              {codeText} ({ev.code})
            </Chip>
          )}
          {ev.remote_number && (
            <Chip hint={FIELD_HELP.remote_number}>{ev.remote_number}</Chip>
          )}
          {duration && <Chip hint={FIELD_HELP.duration_ms}>Took {duration}</Chip>}
          {ev.direction && (
            <Chip>{ev.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</Chip>
          )}
        </div>

        <TechnicalDetails ev={ev} />
      </div>
    </div>
  );
}

function SessionTimeline({ sessionId, range }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyProblems, setOnlyProblems] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          agent_session_id: sessionId,
          from: range.from,
          to: range.to,
          limit: '2000',
        });
        const resp = await getApiCall(
          `${API_WEBRTC_SIP_TRACE_EVENTS}?${params.toString()}`,
          {},
        );
        if (!cancelled) setEvents(Array.isArray(resp?.rows) ? resp.rows : []);
      } catch (e) {
        if (!cancelled) {
          toast.error(typeof e === 'string' ? e : 'Failed to load WebRTC trace');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, range.from, range.to]);

  const shown = useMemo(
    () => (onlyProblems ? events.filter((e) => e.outcome === 'failure') : events),
    [events, onlyProblems],
  );

  if (loading) return <Loader />;
  if (events.length === 0) {
    return (
      <div className="py-6 text-center text-sm font-medium text-muted-foreground">
        Nothing was recorded for this session.
      </div>
    );
  }

  const problems = events.filter((e) => e.outcome === 'failure').length;

  return (
    <div>
      {problems > 0 && (
        <label className="mb-3 inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-muted-foreground">
          <input
            type="checkbox"
            checked={onlyProblems}
            onChange={(e) => setOnlyProblems(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer"
          />
          Show only the {problems} problem{problems === 1 ? '' : 's'}
        </label>
      )}
      {shown.map((ev, i) => (
        <TraceEvent
          key={`${ev.ts}-${ev.seq}-${i}`}
          ev={ev}
          isLast={i === shown.length - 1}
        />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- summary

const Metric = ({ Icon, value, label, hint, color }) => (
  <div
    title={hint || undefined}
    className={cn(
      'flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border/60 bg-card px-2.5 py-2',
      hint && 'cursor-help',
    )}
  >
    <span
      className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md"
      style={{ background: `${color}18`, color }}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.3} />
    </span>
    <div className="min-w-0">
      <div className="text-sm font-bold leading-tight text-foreground">{value}</div>
      <div className="truncate text-[10.5px] font-medium text-muted-foreground">
        {label}
      </div>
    </div>
  </div>
);

function SessionCard({ row, range, isOpen, onToggle }) {
  const failures = Number(row.failures) || 0;
  const healthy = failures === 0;
  const lastProblem = describeEvent(row.last_error_event);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border transition-all',
        isOpen ? 'border-primary/30 shadow-md' : 'border-border shadow-sm',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full cursor-pointer items-center gap-3.5 px-4 py-3.5 text-left"
      >
        <span
          className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
          style={{
            background: healthy ? '#d1fae5' : '#fee2e2',
            color: healthy ? '#047857' : '#dc2626',
          }}
        >
          {healthy ? (
            <CheckCircle2 className="h-4 w-4" strokeWidth={2.3} />
          ) : (
            <AlertTriangle className="h-4 w-4" strokeWidth={2.3} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold">
            {healthy ? 'WebRTC worked normally' : `${failures} problem${failures === 1 ? '' : 's'} found`}
          </div>
          <div className="text-xs font-medium text-muted-foreground">
            {fmtDateTime(row.first_event_at)} → {fmtTime(row.last_event_at)}
            {row.extension ? ` · Ext ${row.extension}` : ''}
          </div>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="inline-flex flex-shrink-0 text-muted-foreground"
        >
          <ChevronDown className="h-4 w-4" strokeWidth={2.4} />
        </motion.span>
      </button>

      <div className="flex flex-wrap gap-2 px-4 pb-3.5">
        <Metric
          Icon={Wifi}
          color="#0891b2"
          value={row.registers_ok || 0}
          label="Sign-ins"
          hint="How many times the extension successfully signed in to the phone server during this session."
        />
        <Metric
          Icon={PhoneIncoming}
          color="#1d4ed8"
          value={row.invites_received || 0}
          label="Calls reached agent"
          hint="Calls that actually arrived at the agent's browser. If the call engine sent more than this, those calls never reached the agent."
        />
        <Metric
          Icon={Radio}
          color="#047857"
          value={row.calls_answered || 0}
          label="Connected"
          hint="Calls that were answered and connected."
        />
        <Metric
          Icon={AlertTriangle}
          color={failures ? '#dc2626' : '#94a3b8'}
          value={failures}
          label="Problems"
          hint="Steps that failed — sign-in failures, blocked microphone, audio problems and so on."
        />
      </div>

      {!healthy && row.last_error && (
        <div className="mx-4 mb-3.5 rounded-lg bg-red-50 px-3 py-2 text-[11.5px] font-medium text-red-700">
          <span className="font-bold">Last problem — {lastProblem.label}:</span>{' '}
          {row.last_error}
        </div>
      )}

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-4 pb-4 pt-3.5">
              <SessionTimeline sessionId={row.agent_session_id} range={range} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ------------------------------------------------------------------- modal

const SipTraceModal = ({ show, onHide, agentId, agentName, agentNumber }) => {
  const [bucketKey, setBucketKey] = useState(DATE_BUCKETS[0].key);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSession, setOpenSession] = useState(null);

  const range = useMemo(
    () => bucketRange(DATE_BUCKETS.find((b) => b.key === bucketKey) || DATE_BUCKETS[0]),
    [bucketKey],
  );

  const fetchData = useCallback(
    async (key) => {
      if (!agentId) return;
      const { from, to } = bucketRange(
        DATE_BUCKETS.find((b) => b.key === key) || DATE_BUCKETS[0],
      );
      setLoading(true);
      setOpenSession(null);
      try {
        const params = new URLSearchParams({
          agent_id: String(agentId),
          from,
          to,
          limit: '100',
        });
        const resp = await getApiCall(
          `${API_WEBRTC_SIP_TRACE_SESSIONS}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : 'Failed to load WebRTC trace');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [agentId],
  );

  useEffect(() => {
    if (show && agentId) {
      setBucketKey(DATE_BUCKETS[0].key);
      fetchData(DATE_BUCKETS[0].key);
    }
    if (!show) {
      setRows([]);
      setOpenSession(null);
    }
  }, [show, agentId, fetchData]);

  const title = agentName || `Agent #${agentId}`;

  return (
    <Dialog open={!!show} onOpenChange={(o) => !o && onHide?.()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Radio className="h-5 w-5" strokeWidth={2.3} />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-base">{title}</DialogTitle>
              <div className="text-xs font-medium text-muted-foreground">
                WebRTC trace{agentNumber ? ` · ${agentNumber}` : ''}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {DATE_BUCKETS.map((b) => {
            const active = b.key === bucketKey;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => {
                  setBucketKey(b.key);
                  fetchData(b.key);
                }}
                disabled={loading}
                className={cn(
                  'inline-flex cursor-pointer items-center rounded-md px-3.5 py-1.5 text-xs font-semibold ring-1 ring-inset transition-all',
                  active
                    ? 'bg-primary text-primary-foreground ring-primary/40 shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)]'
                    : 'bg-card text-foreground ring-border/60 shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] hover:bg-accent hover:text-accent-foreground',
                  loading && 'cursor-not-allowed opacity-60',
                )}
              >
                {b.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-5 py-10 text-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <CalendarClock className="h-6 w-6" strokeWidth={2} />
            </span>
            <div className="text-sm font-medium text-muted-foreground">
              This agent’s WebRTC phone was not used in this period.
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {rows.map((row) => (
              <SessionCard
                key={row.agent_session_id}
                row={row}
                range={range}
                isOpen={openSession === row.agent_session_id}
                onToggle={() =>
                  setOpenSession(
                    openSession === row.agent_session_id ? null : row.agent_session_id,
                  )
                }
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SipTraceModal;
